import Divider from '@/components/Divider';
import { MusicUploadSelectIcon, UploadIcon } from '@/components/Icons';
import Show from '@/components/Show';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAnimatedBorder } from '@/hooks/useAnimationBorder';
import { useSessionStore } from '@/hooks/useSessionStore';
import { getUploadSignUrl } from '@/service';
import { useCatalogOverviewStore } from '@/state/catalog-store';
import useGeneratorStore, {
  defaultGeneratorFormValues,
  EditorTypeEnum,
  GeneratorTypeEnums,
  R3imagineStatusType,
  RecordingStatusEnum,
} from '@/state/generator-store';
import { cn } from '@/utils';
import useFlags from '@jenmusic/feature-flag';
import { useRequest } from 'ahooks';
import { FileMusic, Play, Trash2 } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useInstrumentsDialog } from '../../../hooks/useInstrumentsDialog';
import { useR3ImageDialog } from '../../../hooks/useR3ImageDialog';
import { usePlayMusic } from '../../PreviewPlayerCard/usePlayMusic';
import DurationInput from '../new/DurationInput';
import FolderInput from '../new/FolderInput';
import QualityInput from '../new/QualityInput';
import ModelVersionInput from '../new/ModelVersionInput';
import PromptInputContainer from './PromptInputContainer';

const SimplePromptInput: React.FC = () => {
  const [isOnFocus, setIsOnFocus] = useState(false);
  const [currentLineCount, setCurrentLineCount] = useState(1);
  const [hasChangeLine, setHasChangeLine] = useState(false);

  const {
    register,
    editorType,
    setEditorType,
    editorSelectedInstrument,
    generatorType,
    setGeneratorType,
    currentRecordFile,
    r3ImageStatus,
    setR3ImageStatus,
    setRecordingStatus,
    setCurrentRecordFile,
    hummingAudioSelected,
    setFormValues,
    generatorForm,
  } = useGeneratorStore();

  const sessionStore = useSessionStore();
  const { session } = sessionStore;

  const { show: showR3ImageDialog, R3ImageDialogComponent } = useR3ImageDialog(session);

  const { catalogOverviewStore } = useCatalogOverviewStore();
  const isNewUser = Object.values(catalogOverviewStore).every((catalogNum) => catalogNum === 0);
  const PromptContainer = useRef<HTMLDivElement>(null);

  const { start, clear } = useAnimatedBorder(PromptContainer, {});

  useEffect(() => {
    if (isNewUser) {
      start();
    } else {
      clear();
    }
  }, [isNewUser]);

  const { showNewGenerator, showModelVersionSelector } = useFlags();

  const { show: showVisualizer, hide: hideInstruments, DialogComponent: InstrumentsDialog } = useInstrumentsDialog();

  const handleUploadClick = () => {
    if (r3ImageStatus !== R3imagineStatusType.WaitingGenerate) {
      showR3ImageDialog();
    }
  };

  const [pollingCount, setPollingCount] = useState(0);
  const {
    run: getUploadSignUrlRun,
    cancel: getUploadSignUrlCancel,
    data: getUploadSignUrlData,
  } = useRequest(getUploadSignUrl, {
    manual: true,
    defaultParams: [
      {
        id: String(hummingAudioSelected?.id),
      },
    ],
    onSuccess: (res) => {
      setCurrentRecordFile({
        ...currentRecordFile,
        src: res?.data?.sign_url,
      });
      getUploadSignUrlCancel();
    },
    onBefore: () => {
      setPollingCount((count) => {
        const nextCount = count + 1;
        if (nextCount >= 10) {
          getUploadSignUrlCancel();
        }
        return nextCount;
      });
    },
    pollingInterval: 1000,
    pollingErrorRetryCount: 10,
  });

  useEffect(() => {
    if (hummingAudioSelected?.id) {
      getUploadSignUrlRun({
        id: String(hummingAudioSelected.id),
      });
    }
    return () => {
      getUploadSignUrlCancel();
    };
  }, [hummingAudioSelected?.id]);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const lineCount = useMemo(() => {
    const textarea = textareaRef.current?.querySelector('textarea');
    if (!textarea) return 1;

    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(computedStyle.lineHeight);
    const paddingTop = parseFloat(computedStyle.paddingTop);
    const paddingBottom = parseFloat(computedStyle.paddingBottom);

    const lines = Math.floor((textarea.scrollHeight - paddingTop - paddingBottom) / lineHeight);
    return Math.max(1, lines);
  }, [textareaRef.current?.querySelector('textarea')?.value]);

  const renderPlaceholder = useMemo(() => {
    const textValue = textareaRef.current?.querySelector('textarea')?.value || generatorForm?.watch('prompt');

    if (isOnFocus || textValue) {
      return null;
    }
    if (r3ImageStatus === R3imagineStatusType.WaitingGenerate) {
      return (
        <div className={cn('absolute left-[85px] pointer-events-none', currentLineCount >= 3 ? 'mt-[30px]' : 'mt-[42px]')}>
          <span className="text-[#9d9da6] text-[14px] font-normal">
            Enter a prompt or turn your track{' '}
            <button
              className="text-jen-orange underline cursor-pointer bg-transparent border-none p-0 inline pointer-events-auto"
              onClick={(e) => {
                e.preventDefault();
                setEditorType(EditorTypeEnum.instrument);
                showVisualizer();
              }}
            >
              into an instrument
            </button>
          </span>
        </div>
      );
    }
    return (
      <div className={cn('absolute left-[82px]  pointer-events-none', currentLineCount >= 3 ? 'top-[32px]' : 'top-[48px]')}>
        <span className="text-[#9d9da6] text-[14px] font-normal">Imagine "Rock and Roll music..."</span>
      </div>
    );
  }, [r3ImageStatus, isOnFocus, textareaRef.current?.querySelector('textarea')?.value, currentLineCount]);

  const { play, pause, reset, status, audioRef, isPlaying, duration } = usePlayMusic(currentRecordFile?.src ?? '');

  const handlePlayPause = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    showR3ImageDialog();
  };

  const handleOnDelete = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isPlaying) {
      await pause();
    }
    setGeneratorType(GeneratorTypeEnums.TEXT);
    setFormValues(defaultGeneratorFormValues);
    setEditorType(EditorTypeEnum.text);
    setRecordingStatus(RecordingStatusEnum.WaitingRecording);
    setR3ImageStatus(R3imagineStatusType.WaitingUpload);
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target;
    const value = textarea.value;
    const textareaWidth = textarea.clientWidth;
    const computedStyle = window.getComputedStyle(textarea);
    const fontSize = parseFloat(computedStyle.fontSize);
    const paddingLeft = parseFloat(computedStyle.paddingLeft);
    const paddingRight = parseFloat(computedStyle.paddingRight);
    const availableWidth = textareaWidth - paddingLeft - paddingRight;

    const lines = value.split('\n').reduce((acc, line) => {
      const tempSpan = document.createElement('span');
      tempSpan.style.font = computedStyle.font;
      tempSpan.style.fontSize = computedStyle.fontSize;
      tempSpan.style.whiteSpace = 'pre';
      tempSpan.textContent = line;
      document.body.appendChild(tempSpan);
      const lineWidth = tempSpan.offsetWidth;
      document.body.removeChild(tempSpan);

      return acc + Math.max(1, Math.ceil(lineWidth / availableWidth));
    }, 0);

    setCurrentLineCount(Math.max(1, lines));
    if (lines > 3) {
      setHasChangeLine(true);
    }
    register('prompt').onChange(e);
  };

  return (
    <PromptInputContainer PromptContainer={PromptContainer} lineCount={lineCount} className="flex-grow h-24">
      <>
        <div
          className={cn(
            'flex flex-col cursor-pointer items-center justify-center gap-2 text-sm transition-colors duration-200 hover:text-jen-orange h-[80px]',
            (currentLineCount > 3 || hasChangeLine) && 'mt-[20px]',
          )}
          onClick={handleUploadClick}
        >
          <Show when={r3ImageStatus !== R3imagineStatusType.WaitingGenerate}>
            <>
              <UploadIcon className="w-5 h-5" />
              <span className="uppercase text-[12px] text-[#9D9DA6] hover:text-jen-orange">UPLOAD</span>
            </>
          </Show>
          <Show when={r3ImageStatus === R3imagineStatusType.WaitingGenerate}>
            <div className="flex flex-col gap-y-1.5">
              <div
                className={cn(
                  'text-jen-orange flex justify-center',
                  r3ImageStatus !== R3imagineStatusType.WaitingGenerate ? 'cursor-pointer' : 'pointer-events-none',
                )}
              >
                <FileMusic size={20} className="w-5 h-5 transition-colors hover:text-white" />
              </div>
              <div className="flex justify-center gap-1">
                <button
                  className="bg-black rounded-full hover:bg-zinc-700 transition-colors p-1"
                  aria-label="Delete"
                  onClick={handleOnDelete}
                >
                  <Trash2 size={16} className="w-3 h-3 text-zinc-400 transition-colors hover:text-white" />
                </button>
                <button
                  className="bg-black rounded-full hover:bg-zinc-700 transition-colors p-1"
                  aria-label="Unlock"
                  onClick={handlePlayPause}
                >
                  <Play size={16} className="w-3 h-3 text-zinc-400 transition-colors hover:text-white" />
                </button>
              </div>
            </div>
          </Show>
        </div>
        <Divider className={cn((currentLineCount > 3 || hasChangeLine) && 'top-[20px]')} />
        <div className="w-full ml-2 mt-3 h-[60px]">
          <div className="text-xs text-gray-400">
            <span
              className={cn(
                'font-pressura-mono text-[11px] font-normal leading-[1.5] text-left text-jen-orange left-3 cursor-pointer transition-opacity duration-200',
                editorType === EditorTypeEnum.text ? 'text-jen-orange' : 'text-jen-orange-60 opacity-80 hover:opacity-100',
              )}
              onClick={() => {
                setEditorType(EditorTypeEnum.text);
              }}
            >
              PROMPT
            </span>
            <Show when={generatorType === GeneratorTypeEnums.R3IMAGINE}>
              <span
                className={cn(
                  'font-pressura-mono text-[10px] font-normal leading-[1.5] text-left text-jen-orange left-3 ml-2 cursor-pointer transition-opacity duration-200',
                  editorType === EditorTypeEnum.instrument ? 'text-jen-orange' : 'text-jen-orange-60 opacity-80 hover:opacity-100',
                )}
                onClick={() => {
                  setEditorType(EditorTypeEnum.instrument);
                }}
              >
                INSTRUMENT
              </span>
            </Show>
          </div>
          <Show when={editorType === EditorTypeEnum.text}>
            <div ref={textareaRef as any}>
              <Textarea
                className="min-h-[70px] w-full h-full bg-transparent border-none text-[14px] font-normal leading-[1.35] text-left text-white focus:outline-none resize-none overflow-y-auto break-words px-0 py-1"
                placeholder=""
                style={{
                  boxShadow: 'none',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  maskImage: currentLineCount >= 3 ? 'linear-gradient(to top, rgba(0, 0, 0, 1) 50%, rgba(0, 0, 0, 0) 100%)' : 'none',
                  marginTop: currentLineCount >= 3 ? '-12px' : '0',
                }}
                {...register('prompt')}
                autoCorrect="off"
                autoComplete="off"
                autoCapitalize="off"
                onFocus={(e) => {
                  setIsOnFocus(true);
                }}
                onBlur={(e) => {
                  setIsOnFocus(false);
                }}
                onChange={handleTextareaChange}
                spellCheck={false}
              />
              {renderPlaceholder}
            </div>
          </Show>
          <Show when={editorType === EditorTypeEnum.instrument}>
            <Button
              variant="ghost"
              className="w-[80%] bg-[#111111] hover:bg-[#222222] text-white font-normal justify-between group transition-all duration-300 ease-in-out"
              onClick={showVisualizer}
            >
              <span>{editorSelectedInstrument ?? 'Select Instrument'}</span>
              <MusicUploadSelectIcon />
            </Button>
          </Show>
        </div>
        <Show when={showNewGenerator}>
          <div className={cn('flex items-center gap-6', currentLineCount > 3 && 'mt-[32px]')}>
            <div className="flex items-center gap-4 text-sm">
              <DurationInput />
              {!isNewUser && showModelVersionSelector && <ModelVersionInput />}
              {!isNewUser && <QualityInput />}
              {!isNewUser && <FolderInput />}
            </div>
          </div>
        </Show>
        <R3ImageDialogComponent />
        <InstrumentsDialog />
      </>
    </PromptInputContainer>
  );
};

export default SimplePromptInput;
